import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Story ideas — content prompts an affiliate can use as the spine of a
// post or thread. Different from sample posts (which are fully written)
// — these are the angles or arcs.
//
// API contract:
//   GET /affiliate/content/story-ideas →
//     { items: [{ id, title, body, programs:[] }] }
export default function AssetsStoryIdeas() {
  const [ideas, setIdeas] = useState(null);
  const [program, setProgram] = useProgramFromQuery();
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/story-ideas", { auth: true });
        const items = res?.items || res?.storyIdeas || [];
        if (!cancelled) setIdeas(items);
      } catch {
        if (!cancelled) setIdeas([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!ideas) return [];
    const set = new Set();
    ideas.forEach((i) => (i.programs || []).forEach((p) => set.add(p)));
    return Array.from(set);
  }, [ideas]);

  const visible = useMemo(() => {
    if (!ideas) return [];
    const q = query.trim().toLowerCase();
    return ideas.filter((i) => {
      if (program !== "all" && !(i.programs || []).includes(program)) return false;
      if (q && !(`${i.title || ""} ${i.body || ""}`).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [ideas, program, query]);

  if (ideas === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="STORY" italic="ideas." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Content angles and arc prompts — use them as the spine of a post or thread, layer in your voice on top.
      </p>
      <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search story ideas…"
          data-testid="story-ideas-search"
          style={{ padding: "12px 14px", fontSize: 14, background: C.pure, border: `1px solid ${C.warmGray3}`, outline: "none" }}
        />
        {programs.length > 1 && (
          <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="story-ideas-program" />
        )}
      </div>
      <div data-testid="story-ideas-list" style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        {visible.length === 0 ? (
          <div data-testid="story-ideas-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No story ideas match these filters.
          </div>
        ) : (
          visible.map((i) => (
            <article
              key={i.id || i._id}
              data-testid={`story-idea-${i.id || i._id}`}
              style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 22 }}
            >
              <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", lineHeight: 1.3 }}>
                  {i.title}
                </h3>
                <CopyButton text={`${i.title}\n\n${i.body}`} testId={`story-idea-copy-${i.id || i._id}`} label="COPY" />
              </header>
              {i.body && (
                <p style={{ marginTop: 10, fontSize: 14, lineHeight: 1.7, color: C.ink, whiteSpace: "pre-wrap" }}>
                  {i.body}
                </p>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
}
